import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandMore,
    ExpandLess,
    VolunteerActivism,
    AccountCircle,
    BugReport,
    DarkMode,
    LightMode
} from '@mui/icons-material';
import { useThemeMode } from '../../../contexts/ThemeModeContext';
import { useFeedbackModal } from '../../../contexts/FeedbackModalContext';
import { MobileSubmenuItem } from './MobileSubmenuItem';

function MobileUserActions({ user, onClose, onReportIssue }) {
    return (
        <Box sx={{ p: 2, mt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {user ? (
                <>
                    <Button
                        onClick={() => {
                            onClose();
                            onReportIssue();
                        }}
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        startIcon={<BugReport />}
                        sx={{ fontWeight: 700 }}
                    >
                        Report Issue
                    </Button>
                    <Button
                        component={Link}
                        to="/dashboard"
                        onClick={onClose}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<AccountCircle />}
                        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                        My Dashboard
                    </Button>
                </>
            ) : (
                <Button
                    component={Link}
                    to="/login"
                    onClick={onClose}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<AccountCircle />}
                    sx={{ fontWeight: 700 }}
                >
                    Login
                </Button>
            )}
            <Button
                component={Link}
                to="/donate"
                onClick={onClose}
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={<VolunteerActivism />}
                sx={{ fontWeight: 700 }}
            >
                Donate
            </Button>
        </Box>
    );
}

MobileUserActions.propTypes = {
    user: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onReportIssue: PropTypes.func.isRequired,
};

/**
 * MobileDrawer Component
 * Responsive hamburger drawer navigation, sub-menus, and mobile action buttons.
 */
export function MobileDrawer({
    open,
    onClose,
    items = [],
    isItemRegistered,
    user
}) {
    const location = useLocation();
    const { resolvedMode, toggleTheme } = useThemeMode();
    const { openFeedbackModal } = useFeedbackModal();
    const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState({});

    const handleMobileSubmenuToggle = (index) => {
        setMobileSubmenuOpen((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    // Close mobile menu on route change
    useEffect(() => {
        if (open) onClose();
        if (Object.keys(mobileSubmenuOpen).length > 0) setMobileSubmenuOpen({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            ModalProps={{
                keepMounted: true, // Better mobile performance
            }}
            aria-hidden={!open}
            sx={{
                '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: 240,
                    visibility: open ? 'visible' : 'hidden',
                },
            }}
        >
            <Box sx={{ width: 240, bgcolor: 'background.paper', height: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="h6"
                        component={Link}
                        to="/"
                        sx={{
                            fontWeight: 800,
                            color: 'primary.main',
                            textDecoration: 'none',
                        }}
                    >
                        TAS SKI TEAM
                    </Typography>

                    <Tooltip title="Toggle theme" arrow>
                        <IconButton
                            onClick={toggleTheme}
                            aria-label="Toggle Theme Mode"
                            color="inherit"
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            {resolvedMode === 'dark' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close menu" arrow>
                        <IconButton onClick={onClose} aria-label="Close navigation menu">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <List>
                    {items.map((item, index) => {
                        const hasDivider = Boolean(item.submenu?.some(i => i.isDivider));

                        return (
                            <Box key={item.label}>
                                {item.submenu ? (
                                    <>
                                        <ListItemButton
                                            onClick={() => handleMobileSubmenuToggle(index)}
                                            aria-expanded={Boolean(mobileSubmenuOpen[index])}
                                        >
                                            <ListItemText
                                                primary={item.label}
                                                slotProps={{ primary: { fontWeight: 600 } }}
                                            />
                                            {mobileSubmenuOpen[index] ? <ExpandLess /> : <ExpandMore />}
                                        </ListItemButton>
                                        <Collapse in={mobileSubmenuOpen[index]} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {item.submenu.map((subItem, subIndex) => (
                                                    <MobileSubmenuItem
                                                        key={subItem.label || `subItem-${subIndex}`}
                                                        subItem={subItem}
                                                        subIndex={subIndex}
                                                        hasDivider={hasDivider}
                                                        onClose={onClose}
                                                        isItemRegistered={isItemRegistered}
                                                    />
                                                ))}
                                            </List>
                                        </Collapse>
                                    </>
                                ) : (
                                    <ListItemButton component={Link} to={item.path} onClick={onClose}>
                                        <ListItemText
                                            primary={item.label}
                                            slotProps={{ primary: { fontWeight: 600 } }}
                                        />
                                    </ListItemButton>
                                )}
                            </Box>
                        );
                    })}

                    <MobileUserActions
                        user={user}
                        onClose={onClose}
                        onReportIssue={() => openFeedbackModal('bug')}
                    />
                </List>
            </Box>
        </Drawer>
    );
}

MobileDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(PropTypes.object),
    isItemRegistered: PropTypes.func,
    user: PropTypes.object,
};

export default MobileDrawer;
